import { leadsEndpoints } from "@/config/endpoints";
import axiosInstance from "@/lib/axios";
import { queryClient } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useActiveLeadFilterParams } from "./useActiveLeadFilterParams";

export default function useCurrentLead(
  leadId: number | null | string | undefined
) {
  // Gate the fetch on a real leadId — otherwise we fire `GET /leads/undefined`
  // and pollute the console + server logs with 400s. Common case: a parent
  // component reads `currentLeadId` from Redux which is `undefined` until
  // the user opens a lead, and the consumer renders this hook anyway.
  const enabled = leadId != null && leadId !== "" && leadId !== "undefined";

  // Under an ANCHOR ("Verified") filter the drawer must open the deal the
  // filter actually MATCHED. It used to re-derive that client-side from stage
  // order alone, which ignores the Intake/Country filter entirely — so a lead
  // with deals in two intakes opened on the one the filter excluded. Forwarding
  // just the anchor + custom-field keys lets the server answer with the SAME
  // representative the list and the count used (`matchedChildId`).
  //
  // Only those keys ride along, so the detail cache isn't fragmented by every
  // unrelated filter, and nothing is sent at all when no anchor is active.
  // Route-AWARE: stage tabs and Favorite Views keep their filters in a
  // different slice than /leads. Reading only the /leads slice made this
  // forward nothing on exactly the surfaces the bug was reported on.
  const allParams = useActiveLeadFilterParams();
  const anchorParams = useMemo(() => {
    const out: Record<string, any> = {};
    if (!allParams?.verified) return out;
    out.verified = allParams.verified;
    for (const [k, v] of Object.entries(allParams)) {
      if (k.startsWith("cf_") || k.startsWith("cfPair_")) out[k] = v;
    }
    // The date range rides along too (the serializer sends both or neither):
    // the server's representative pick is WINDOWED — under "Count each student
    // once" it is a snapshot at the range END, and even the legacy anchor ranks
    // in-window reaches — so without the range GET /leads/:id would answer with
    // a different deal than the list row it was opened from (e.g. the deal that
    // was live at month end but is rejected today). The queryKey includes these
    // params, so the detail cache stays coherent per range.
    if (allParams.startDate && allParams.endDate) {
      out.startDate = allParams.startDate;
      out.endDate = allParams.endDate;
    }
    return out;
  }, [allParams]);
  const hasAnchor = Boolean(anchorParams.verified);

  const query = useQuery({
    queryKey: ["get-leads", leadId, hasAnchor ? anchorParams : null],
    enabled,
    queryFn: async () => {
      const res = await axiosInstance.get(`${leadsEndpoints}/${leadId}`, {
        params: hasAnchor ? anchorParams : undefined,
      });
      return res.data.lead;
    },
  });

  return {
    lead: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
export const refetchCurrentLead = async () => {
  await queryClient.invalidateQueries({ queryKey: ["get-leads"] });
};
